import { z } from "zod";
export const sectionRoleSchema = z.enum([
    "intro",
    "verse",
    "chorus",
    "bridge",
    "solo",
    "breakdown",
    "outro",
    "custom",
]);
export const sectionAddedPayloadSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    role: sectionRoleSchema,
    order: z.number().int().min(0),
    prompt: z.string(),
});
export const sectionPromptChangedPayloadSchema = z.object({
    sectionId: z.string().min(1),
    newPrompt: z.string(),
});
export const sectionAudioBoundPayloadSchema = z
    .object({
    sectionId: z.string().min(1),
    assetHash: z.string().regex(/^[0-9a-f]{64}$/),
    startMs: z.number().int().min(0),
    endMs: z.number().int().min(1),
})
    .refine((p) => p.endMs > p.startMs, {
    message: "endMs must be > startMs",
    path: ["endMs"],
});
export const sectionAudioReboundPayloadSchema = z
    .object({
    sectionId: z.string().min(1),
    newAssetHash: z.string().regex(/^[0-9a-f]{64}$/),
    oldAssetHash: z.string().regex(/^[0-9a-f]{64}$/),
    startMs: z.number().int().min(0),
    endMs: z.number().int().min(1),
})
    .refine((p) => p.endMs > p.startMs, {
    message: "endMs must be > startMs",
    path: ["endMs"],
});
/**
 * A single section-variation candidate — an alternative take for a section,
 * each with its own content-addressed clip. Carried in the
 * `SectionVariationsGenerated` payload; the reducer materializes these onto
 * `Section.variations` (adding the derived `promoted` flag).
 */
export const sectionVariationSchema = z.object({
    variationId: z.string().min(1),
    assetHash: z.string().regex(/^[0-9a-f]{64}$/),
    durationMs: z.number().int().positive(),
    label: z.string().min(1),
});
/**
 * A batch of alternative-take candidates arrived for a section. Each worker
 * job in a variation batch emits one of these with a single-element
 * `variations` array, all sharing the same `batchId`; the reducer merges them
 * (deduping by `variationId`). Does NOT touch the section's active `audio`.
 */
export const sectionVariationsGeneratedPayloadSchema = z.object({
    sectionId: z.string().min(1),
    batchId: z.string().min(1),
    variations: z.array(sectionVariationSchema).min(1).max(8),
});
/**
 * The user promoted a variation to be the section's active clip. Reuses the
 * `SectionAudioRebound` mechanics in the reducer (swap `audio`, push the prior
 * binding to `audio.history`). `previousAssetHash` is the section's current
 * `audio.assetHash`, or `null` when the section had no clip yet.
 */
export const sectionVariationPromotedPayloadSchema = z.object({
    sectionId: z.string().min(1),
    variationId: z.string().min(1),
    newAssetHash: z.string().regex(/^[0-9a-f]{64}$/),
    previousAssetHash: z
        .string()
        .regex(/^[0-9a-f]{64}$/)
        .nullable(),
    durationMs: z.number().int().positive(),
});
/**
 * Genre-shift kinds (spec §4.3). `shiftKind` categorizes the shift for the UI;
 * the open-ended `descriptor` carries the actual destination.
 */
export const SHIFT_KINDS = ["genre", "era", "acoustic", "custom"];
/**
 * `SectionGenreShifted` (musical, v1) — NEW (spec §4.3). Records a genre/era/
 * style reinterpretation requested for one section; the reducer materializes it
 * onto `Section.genreShift` and `buildSectionPrompt` folds it into the prompt.
 *
 * A `discriminatedUnion("mode")` mirroring `BandSonicTraitsOverridden`: `set`
 * applies the shift, `clear` reverts to the section's base sound — "go back to
 * normal" is a NEW event, never a delete (invariant #1). `descriptor` is
 * length-bounded free text, not an enum: genre-shift is too open for a closed
 * vocabulary. Born at v1 — no upcaster.
 */
export const sectionGenreShiftedPayloadSchema = z.discriminatedUnion("mode", [
    z.object({
        mode: z.literal("set"),
        sectionId: z.string().min(1),
        shiftKind: z.enum(SHIFT_KINDS),
        descriptor: z.string().min(1).max(120),
    }),
    z.object({
        mode: z.literal("clear"),
        sectionId: z.string().min(1),
    }),
]);
//# sourceMappingURL=section.js.map